import { type Annotation } from "building-types/annotation/annotationTypes";
import { AnnotationLineType, AnnotationType, PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import type { Asset } from "building-types/entities/asset";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { ReferenceGeom } from "building-types/entities/referenceGeom";
import type {
  AnnotationHandle,
  ContentTemplateInstanceHandle,
  RoomLayoutInstanceHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { Room, RoomGeometry } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import {
  type BuildingGenerationIssue,
  BuildingGenerationIssueType,
} from "building-types/settings/buildingGenerationIssue";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { ElevationReferenceFrame } from "building-types/views/elevationReferenceFrame";
import type { ContentTemplateInstance, DrawingTemplateInstance } from "building-types/views/planSets";
import type { RWTable, Table } from "common/data/table";
import { radianToDegree } from "common/utils/math";
import { isGeometryRef } from "doc/roofSystemTools";
import {
  annotationMirror,
  annotationRotationForbidden,
  annotationTransform,
  hostEdgeOriginAngle,
  hostEdgeToGlobalTransform,
  lineworkNew,
  noteNew,
} from "doc/utils/annotation";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { getLongestLineIndex } from "geom/line2Polyline2";
import { mat3Dot, type Mat3, mat3Ident, mat3Translate, mat3RotateDegrees, mat3Reduce } from "geom/mat3";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2Edge, polyLine2EdgeEval, polyLine2EdgeTangent } from "geom/polyLine2";
import { vec2Perp, vec2AngleBetweenCWSimple } from "geom/vec2";
import type { ContentTemplateInstanceNode } from "../contentTemplates/planSetsEvaluate";
import { levelBelowId } from "../elements/level/levelTools";

function mirrorDrawingTemplateInstanceAnnotation(annotation: Annotation, assetCatalog: Table<Asset>, mirror: boolean) {
  if (mirror) {
    return annotationMirror(annotation, assetCatalog, 0);
  } else {
    return annotation;
  }
}

export function addDrawingTemplateInstanceAnnotation(
  docContentTemplateInstances: Table<ContentTemplateInstance>,
  drawing: DrawingTemplateInstance,
  instanceNode: ContentTemplateInstanceNode,
  parentId: string,
  handle: AnnotationHandle | RoomLayoutInstanceHandle | ContentTemplateInstanceHandle,
  handleAuxiliary: AnnotationHandle | ContentTemplateInstanceHandle | undefined,
  entityInstanceType: EntityInstanceType | undefined,
  contentTemplateAnnotation: { [templateId: string]: Annotation[] },
  mirror: boolean,
  assetCatalog: Table<Asset>,
  targetTable: RWTable<Annotation[]>,
) {
  if (!parentId) {
    return;
  }

  return addDrawingTemplateInstanceAnnotationRecursive(
    docContentTemplateInstances,
    drawing,
    instanceNode,
    parentId,
    handle,
    handleAuxiliary,
    entityInstanceType,
    contentTemplateAnnotation,
    mirror,
    assetCatalog,
    targetTable,
    drawing.labels,
  );
}

function addDrawingTemplateInstanceAnnotationRecursive(
  docContentTemplateInstances: Table<ContentTemplateInstance>,
  drawing: DrawingTemplateInstance,
  instanceNode: ContentTemplateInstanceNode,
  parentId: string,
  handle: AnnotationHandle | RoomLayoutInstanceHandle | ContentTemplateInstanceHandle,
  handleAuxiliary: AnnotationHandle | ContentTemplateInstanceHandle | undefined,
  entityInstanceType: EntityInstanceType | undefined,
  contentTemplateAnnotation: { [templateId: string]: Annotation[] },
  mirror: boolean,
  assetCatalog: Table<Asset>,
  targetTable: RWTable<Annotation[]>,

  drawingInstanceLabels: LabelableEntityBase["labels"],

  parentTransform: Mat3 = mat3Ident(),
  parentRotation: number = 0,
) {
  if (!parentId) {
    return;
  }
  const drawingTransform = mat3Reduce([
    parentTransform,
    mat3Translate(drawing.x, drawing.y),
    mat3RotateDegrees(drawing.rotation),
  ]);
  const drawingRotationDegrees = parentRotation + drawing.rotation;
  targetTable[parentId] = targetTable[parentId] || [];

  for (const annotation of Object.values(instanceNode.templateChildren.annotation)) {
    const mirroredAnnotation = mirrorDrawingTemplateInstanceAnnotation(annotation, assetCatalog, mirror);
    if (!mirroredAnnotation) {
      continue;
    }
    let transformedAnnotation = annotationTransform(
      { ...mirroredAnnotation, handle, handleAuxiliary, entityInstanceType },
      drawingTransform,
      drawingRotationDegrees,
    );

    // Labels from the instance can override labels from individual annotations.
    transformedAnnotation = {
      ...transformedAnnotation,
      labels: { ...transformedAnnotation.labels, ...drawingInstanceLabels },
    };

    targetTable[parentId].push(transformedAnnotation);
  }
  for (const childInstance of Object.values(instanceNode.templateChildren.childInstances)) {
    addDrawingTemplateInstanceAnnotationRecursive(
      docContentTemplateInstances,
      childInstance.instance as DrawingTemplateInstance,
      childInstance,
      parentId,
      handle,
      handleAuxiliary,
      entityInstanceType,
      contentTemplateAnnotation,
      mirror,
      assetCatalog,
      targetTable,
      { ...childInstance.instance.labels, ...drawingInstanceLabels }, // Containers always override labels from their children.
      drawingTransform,
      drawingRotationDegrees,
    );
  }
}

interface HandleAnnotationMirroringParams {
  readonly referenceGeom: Table<ReferenceGeom>;
  readonly annotation: Annotation;
  readonly mirror: boolean;
  readonly mirrorTranslationX: number;
  readonly assetCatalog: Table<Asset>;
  readonly handle?: AnnotationHandle;
}

export function mirrorAnnotation({
  annotation,
  mirror,
  mirrorTranslationX,
  referenceGeom,
  assetCatalog,
  handle: providedHandle,
}: HandleAnnotationMirroringParams): Annotation | undefined {
  const handle: AnnotationHandle = providedHandle ?? { type: "ANNOTATION", id: annotation.id };
  switch (annotation.type) {
    case AnnotationType.LINEWORK:
    case AnnotationType.NOTE:
    case AnnotationType.DETAIL:
    case AnnotationType.LINEAR_DIMENSION: {
      const handleAnnot: Annotation = { ...annotation, handle };

      if (
        !mirror ||
        (annotation.type === AnnotationType.DETAIL && annotation.positioningMethod === PositioningMethod.GLOBAL)
      ) {
        //globally positioned image annotation does not get mirrored
        return handleAnnot;
      } else {
        return annotationMirror(handleAnnot, assetCatalog, mirrorTranslationX);
      }
    }
    default: {
      if (isGeometryRef(annotation.polyLine)) {
        const polyLine = referenceGeom[annotation.polyLine.geomId]?.geom;

        if (!polyLine) {
          return undefined;
        }

        return {
          ...annotation,
          handle,
          polyLine,
        };
      }
    }
  }
  return undefined;
}

interface HandleDrawingTemplateInstanceMirroringParams {
  readonly drawing: DrawingTemplateInstance;
  readonly mirror: boolean;
  readonly mirrorTranslationX: number;
}

export function mirrorDrawingTemplateInstance({
  drawing,
  mirror,
  mirrorTranslationX,
}: HandleDrawingTemplateInstanceMirroringParams): DrawingTemplateInstance {
  if (!mirror) {
    return drawing;
  }

  return {
    ...drawing,
    x: -drawing.x + mirrorTranslationX,
    y: drawing.y,
    rotation: -(drawing.rotation ?? 0),
  };
}

export function annotationTransformToGlobal(
  annotation: Annotation,
  room: RoomGeometry,
  mirror: boolean = false,
  issues: BuildingGenerationIssue[],
): Annotation {
  const validEdgeId = annotation.edgeId && room.edgeIds.includes(annotation.edgeId);
  if (!validEdgeId) {
    issues.push({
      type: BuildingGenerationIssueType.INVALID_ANNOTATION_EDGE_ID,
      edgeId: annotation.edgeId,
      handle: { type: "ANNOTATION", id: annotation.id },
    });
    return annotation;
  }

  const hostGeom = hostEdgeOriginAngle(room, annotation.edgeId, mirror);

  if (!hostGeom) {
    return annotation;
  }
  const toGlobalMat: Mat3 = hostEdgeToGlobalTransform(hostGeom.originX, hostGeom.originY, hostGeom.angle);
  if (annotationRotationForbidden(annotation)) {
    return annotationTransform(annotation, toGlobalMat, 0);
  }
  return annotationTransform(annotation, toGlobalMat, radianToDegree(hostGeom.angle));
}

export function drawingTemplateInstanceTransformToGlobal(
  drawing: DrawingTemplateInstance,
  room: RoomGeometry,
  mirror: boolean = false,
  issues: BuildingGenerationIssue[],
): DrawingTemplateInstance {
  const validEdgeId = drawing.edgeId && room.edgeIds.includes(drawing.edgeId);
  if (!validEdgeId) {
    issues.push({
      type: BuildingGenerationIssueType.INVALID_ANNOTATION_EDGE_ID,
      edgeId: drawing.edgeId,
      handle: { type: "ANNOTATION", id: drawing.id },
    });
    return drawing;
  }

  const hostGeom = hostEdgeOriginAngle(room, drawing.edgeId, mirror);

  if (!hostGeom) {
    return drawing;
  }
  const toGlobalMat: Mat3 = hostEdgeToGlobalTransform(hostGeom.originX, hostGeom.originY, hostGeom.angle);

  const [x, y] = mat3Dot(toGlobalMat, drawing.x, drawing.y);
  return {
    ...drawing,
    x,
    y,
    rotation: drawing.rotation + radianToDegree(hostGeom.angle),
  };
}

export function elevationViewMirrorParameters(
  referenceFrame: ElevationReferenceFrame,
  mirror: boolean,
  mirrorTranslationX: number,
): { mirror: boolean; mirrorTranslationX: number; mirroredReferenceFrame: ElevationReferenceFrame } {
  if (!mirror) {
    return { mirror: false, mirrorTranslationX: 0, mirroredReferenceFrame: referenceFrame };
  }
  switch (referenceFrame) {
    case "LEFT":
      return {
        mirror: true,
        mirrorTranslationX: 0,
        mirroredReferenceFrame: "RIGHT",
      };
    case "RIGHT":
      return {
        mirror: true,
        mirrorTranslationX: 0,
        mirroredReferenceFrame: "LEFT",
      };
    case "BACK":
      return {
        mirror: true,
        mirrorTranslationX: -mirrorTranslationX,
        mirroredReferenceFrame: "BACK", // stays the same
      };
    case "FRONT":
    default:
      return {
        mirror: true,
        mirrorTranslationX,
        mirroredReferenceFrame: "FRONT", // stays the same
      };
  }
}

export function generateLevelAnnotation(
  levels: Table<Level>,
  rooms: Table<Room>,
  planSettings: PlanSettings,
  wallSettings: WallSettings,
) {
  const levelAnnotation: RWTable<Annotation[]> = {};

  for (const roomId in rooms) {
    const room = rooms[roomId];
    const roomLevelIdBelow = levelBelowId(room.level.id, levels);

    if (room.type === RoomType.OPEN_BELOW) {
      if (roomLevelIdBelow && !levelAnnotation[roomLevelIdBelow]) {
        levelAnnotation[roomLevelIdBelow] = [];
      }

      const { enableOpenToAboveAnnotation } = planSettings;

      if (roomLevelIdBelow && enableOpenToAboveAnnotation) {
        const outlineLinework = lineworkNew({
          name: "",
          path: offsetPolyLine2(room.outline, -wallSettings.interiorWallOffset),
          lineType: AnnotationLineType.DASHED_LARGE,
          labels: makeBuiltInLabelTable("addToFloorPlan", "addToBrochurePlan", "addToShowroomPlan"),
        });

        const ceilingOpeningAnnotation = generateCeilingOpeningAnnotation(room);
        levelAnnotation[roomLevelIdBelow].push(outlineLinework, ceilingOpeningAnnotation);
      }

      if (!levelAnnotation[room.level.id]) {
        levelAnnotation[room.level.id] = [];
      }

      // level floor opening edge
      levelAnnotation[room.level.id].push(
        lineworkNew({
          name: "",
          path: offsetPolyLine2(room.outline, -wallSettings.interiorWallOffset),
          labels: makeBuiltInLabelTable("addToFloorPlan", "addToBrochurePlan", "addToShowroomPlan"),
        }),
      );
    }
  }

  return levelAnnotation;
}

export function generateCeilingOpeningAnnotation(room: Room) {
  const polyLine = polyLine2Edge(room.outline, getLongestLineIndex(room.outline));

  /**
   * @NOTE - these values are hardoded to mimic the behavior
   * of the PlanNote SVG element
   */
  const edgeParam = 0.78;
  const edgeOffset = 8;
  const leaderLength = 18;

  const [cX, cY] = polyLine2EdgeEval(polyLine, 0, edgeParam, true);
  const [tX, tY] = polyLine2EdgeTangent(polyLine, 0);
  const [nX, nY] = vec2Perp(tX, tY);

  const posX = cX + nX * edgeOffset;
  const poxY = cY + nY * edgeOffset;

  const leaderLineX = cX - tX * leaderLength;
  const leaderLineY = cY - tY * leaderLength;

  const landingLineX = leaderLineX + nX * edgeOffset + tX * edgeOffset;
  const landingLineY = leaderLineY + nY * edgeOffset + tY * edgeOffset;

  const rotation = radianToDegree(vec2AngleBetweenCWSimple(1, 0, tX, tY));

  return noteNew({
    x: posX,
    y: poxY,
    text: "CEILING OPENING",
    rotation,
    landingLineX,
    landingLineY,
    leaderLineX,
    leaderLineY,
    labels: makeBuiltInLabelTable("addToFloorPlan"),
  });
}
